/**
 * Main AHU design orchestrator
 * Coordinates the creation and configuration of an AHU unit
 *
 * Implementation follows ordered steps:
 * 1. Create AHU entity with base configuration
 * 2. Create up to 2 tunnels only if required by airflow/layout
 * 3. Initialize tunnel width and height to system minimums
 * 4. Create fans for all tunnels
 * 5. Call Tunnel Validation API (iterate until no errors)
 * 6. Resize AHU for coil/filter/rotary if required
 * 7. Call sizing recommendation service
 * 8. Create all remaining sections and lock tunnel dimensions
 * 9. Create accessories (opening, access_door, drain_pan)
 * 10. Align the length of the tunnels for opposite airflow directions
 */
import { dispatchMutation } from '../../utils/dispatch.utils';
import { ahuApi } from '../../services';
import { step1CreateAHU } from './designAHU/steps/step1-createAHU';
import { step2CreateTunnels } from './designAHU/steps/step2-createTunnels';
import { step3InitializeTunnelDimensions } from './designAHU/steps/step3-initializeDimensions';
import { step4CreateFansAndRotaries } from './designAHU/steps/step4-createFansAndRotaries';
import { step5ValidateAndFixTunnels } from './designAHU/steps/step5-validateTunnels';
import { step6ResizeForComponents } from './designAHU/steps/step6-resizeForComponents';
import { step7ApplySizingRecommendations } from './designAHU/steps/step7-applySizingRecommendations';
import { step8CreateRemainingSections } from './designAHU/steps/step8-createRemainingSections';
import { step9CreateAccessories } from './designAHU/steps/step9-createAccessories';
import { step10AlignTunnelsLengthAndWidth } from './designAHU/steps/step10-alignTunnelsLengthAndWidth';
import { resolveDefaultValues } from './designAHU/utils/resolveDefaults.utils';
import { getVolumeForAirStream } from './designAHU.sizer';
import { addRequiredPlenums } from './designAHU/utils/addRequiredPlenums.utils';
import { SizerResult, DesignAHUOptions, DesignAHUResult } from '../../models/bot';
import {
  createProgressCalculator,
  DEFAULT_COEFFICIENTS,
} from './calculateCompletedPercent';
import { delay } from '..';
/**
 * Main design AHU function - orchestrates all steps
 */
export const designAHU = async (
  options: DesignAHUOptions,
): Promise<DesignAHUResult> => {
  let {
    botData,
    ahuParams,
    rotaryParams,
    coilParams,
    accessDoorParams,
    connHands,
    projectId,
    onProgress,
    coefficients = DEFAULT_COEFFICIENTS,
    onStop,
    abortSignal,
    onAhuCreated,
  } = options;
  // Automatically add plenums based on various conditions
  botData = addRequiredPlenums(botData);
  // Initialize progress calculator
  const progressCalculator = createProgressCalculator(onProgress, coefficients);
  let stopped = false;
  // Helper to check if stopped
  const checkStopped = () => {
    if (stopped || abortSignal?.aborted) {
      onStop?.();
      return true;
    }
    return false;
  };
  // Helper to create options with abort signal
  const getOptions = () => ({ signal: abortSignal });
  // State variables
  let ahuId: number = 0;
  let tunnelIds: number[] = [];
  let tunnels: any[] = [];
  let sizerSettings: any = null;
  let ahuParamsResult: any = {};
  try {
    // Initialize progress calculator with calculated total actions based on botData
    // Initialize early with available data - tunnel info will be null initially (handled by calculateTotalActions)
    if (progressCalculator.initialize) {
      progressCalculator.initialize(botData, {
        compiled: false,
      });
    }

    // Resolve all default values early from all sources (localStorage, ahuParams, defaults)
    const defaults = resolveDefaultValues(ahuParams);
    let thickness = defaults.thickness;
    let casingInsulationId = defaults.casingInsulationId;
    let casingInnerlinerId = defaults.casingInnerlinerId;
    let floorInnerlinerId = defaults.floorInnerlinerId;
    // Store IDs calculated once for later use
    const outerlinerId = defaults.outerlinerId;
    const underInsulationId = defaults.underInsulationId;
    const underlinerId = defaults.underlinerId;
    // Step 1: Create AHU entity with base configuration
    ahuId = await step1CreateAHU(botData, projectId, getOptions, onAhuCreated);
    progressCalculator.calculateCompletedPercent(coefficients.create_ahu);
    if (checkStopped()) {
      return { success: false, ahuId: 0, error: 'Process stopped' };
    }
    // Update AHU with final configuration
    await dispatchMutation(
      ahuApi.endpoints.updateAhu,
      {
        id: ahuId,
        body: {
          ahu_main_voltage_id: +botData.voltage_id || 1,
          weather_rating_id: botData.weather_rating_id,
          sa_volume_max: +botData.sa_volume_max,
          tag: botData.tag,
          construction_class_id: botData.construction_class_id,
        },
      },
      getOptions(),
    );
    progressCalculator.calculateCompletedPercent(coefficients.update_ahu);
    // Step 2: Create tunnels (up to 2) if required
    const tunnelResult = await step2CreateTunnels(
      ahuId,
      botData,
      ahuParams,
      getOptions,
    );
    tunnels = tunnelResult.tunnels;
    tunnelIds = tunnelResult.tunnelIds;
    if (checkStopped()) {
      return {
        success: false,
        ahuId,
        tunnelIds: tunnelIds.length > 0 ? tunnelIds : undefined,
        error: 'Process stopped',
      };
    }
    // Step 3: Initialize tunnel dimensions to system minimums
    tunnels = await step3InitializeTunnelDimensions(
      tunnels,
      tunnelIds,
      ahuParams,
      botData,
      getOptions,
    );
    progressCalculator.calculateCompletedPercent(coefficients.update_tunnel);
    if (checkStopped()) {
      return {
        success: false,
        ahuId,
        tunnelIds,
        error: 'Process stopped',
      };
    }
    // Step 4: Create fans for all tunnels
    const result = await step4CreateFansAndRotaries(
      botData,
      tunnels,
      tunnelIds,
      casingInsulationId,
      casingInnerlinerId,
      floorInnerlinerId,
      ahuParams,
      rotaryParams,
      getOptions,
      (coeff) => progressCalculator.calculateCompletedPercent(coeff),
      checkStopped,
    );
    tunnels = result.updatedTunnels;
    // Check for fan errors - stop immediately if any fan has an error
    if (result.rateErrors.length > 0) {
      return {
        success: false,
        ahuId,
        tunnelIds: tunnelIds.length > 0 ? tunnelIds : undefined,
        error: result.rateErrors[0].error, // Special error code to indicate model errors
        sectionErrors: result.rateErrors, // Include fan errors as section errors
      };
    }

    if (checkStopped()) {
      return {
        success: false,
        ahuId,
        tunnelIds,
        error: 'Process stopped',
      };
    }
    // Step 5: Validate tunnels and fix size errors (fans now exist)
    tunnels = await step5ValidateAndFixTunnels(
      tunnels,
      tunnelIds,
      botData.tunnels,
      true,
      getOptions,
      (coeff) => progressCalculator.calculateCompletedPercent(coeff),
    );
    if (checkStopped()) {
      return {
        success: false,
        ahuId,
        tunnelIds,
        error: 'Process stopped',
      };
    }
    // Step 6: Resize AHU for coil/filter/er_wheel if required (for each tunnel separately)
    // Process each tunnel sequentially to maintain shared state (thickness, casingInsulationId)
    // IMPORTANT: All async requests within step 6 must complete before proceeding to step 8
    // Cache sizer results by airflow to avoid duplicate requests
    const sizerCache = new Map<number, SizerResult>();
    // Store per-tunnel ahuParamsResult for step 7 (each tunnel uses its own airflow-specific result)
    const tunnelAhuParamsResults: any[] = [];

    for (let i = 0; i < tunnels.length; i++) {
      // Get airflow for this tunnel to check cache
      const tunnelAirStreamId = botData.tunnels[i]?.air_stream_id ?? 1;
      const airflow = getVolumeForAirStream(botData, tunnelAirStreamId);

      let resizeResult: SizerResult;

      // Check if we have cached data for this airflow
      if (sizerCache.has(airflow)) {
        // Return cached data, but update tunnel reference
        const cachedResult = sizerCache.get(airflow)!;
        resizeResult = {
          ...cachedResult,
          tunnel: tunnels[i], // Use current tunnel, not cached tunnel
        };
        // Still update progress even when using cached data
        progressCalculator.calculateCompletedPercent(DEFAULT_COEFFICIENTS.size);
      } else {
        // Await all requests in step 6 for this tunnel to complete
        resizeResult = await step6ResizeForComponents(
          botData,
          ahuParams,
          tunnels[i], // Pass single tunnel as array
          i,
          thickness,
          casingInsulationId,
          getOptions,
          (coeff) => progressCalculator.calculateCompletedPercent(coeff),
        );

        // Cache the result for this airflow (without tunnel reference to avoid stale data)
        sizerCache.set(airflow, {
          sizerSettings: resizeResult.sizerSettings,
          ahuParamsResult: resizeResult.ahuParamsResult,
          thickness: resizeResult.thickness,
          casingInsulationId: resizeResult.casingInsulationId,
        });
      }

      // Store this tunnel's ahuParamsResult (airflow-specific)
      tunnelAhuParamsResults[i] = resizeResult.ahuParamsResult;

      // Update shared state for next iteration (if any)
      sizerSettings = resizeResult.sizerSettings;
      ahuParamsResult = resizeResult.ahuParamsResult;
      thickness = resizeResult.thickness;
      casingInsulationId = resizeResult.casingInsulationId;

      // Update the tunnel in the array with the potentially resized tunnel
      if (resizeResult.tunnel) {
        tunnels[i] = resizeResult.tunnel;
      }

      if (checkStopped()) {
        return {
          success: false,
          ahuId,
          tunnelIds,
          error: 'Process stopped',
        };
      }
    }
    // All step 6 requests have completed - safe to proceed to step 7 and step 8
    // Step 7: Apply sizing recommendations (each tunnel uses its own airflow-specific ahuParamsResult)
    tunnels = await step7ApplySizingRecommendations(
      tunnels,
      tunnelIds,
      tunnelAhuParamsResults,
      underInsulationId as number,
      underlinerId as number,
      outerlinerId as number,
      getOptions,
      (coeff) => progressCalculator.calculateCompletedPercent(coeff),
    );
    if (checkStopped()) {
      return {
        success: false,
        ahuId,
        tunnelIds,
        error: 'Process stopped',
      };
    }
    // Step 8: Create all sections for each tunnel (fan first, then other sections)
    const step8Result = await step8CreateRemainingSections(
      botData,
      tunnelIds,
      tunnels,
      sizerSettings,
      thickness,
      casingInsulationId,
      casingInnerlinerId,
      floorInnerlinerId,
      ahuParams,
      coilParams,
      connHands,
      ahuParamsResult,
      getOptions,
      (coeff) => progressCalculator.calculateCompletedPercent(coeff),
      checkStopped,
    );
    if (checkStopped()) {
      return {
        success: false,
        ahuId,
        tunnelIds,
        error: 'Process stopped',
      };
    }

    // If there are section errors, return failure but don't throw
    if (step8Result.sectionErrors.length > 0) {
      return {
        success: false,
        ahuId,
        tunnelIds: tunnelIds.length > 0 ? tunnelIds : undefined,
        error: step8Result.sectionErrors[0].error, // Special error code to indicate model errors
        sectionErrors: step8Result.sectionErrors, // Include all section errors
      };
    }

    // Step 10: Create accessories (opening, access_door, drain_pan)
    await step9CreateAccessories(
      botData,
      tunnelIds,
      tunnels,
      thickness,
      casingInsulationId,
      casingInnerlinerId,
      floorInnerlinerId,
      accessDoorParams,
      getOptions,
      (coeff) => progressCalculator.calculateCompletedPercent(coeff),
      checkStopped,
    );
    if (checkStopped()) {
      return {
        success: false,
        ahuId,
        tunnelIds,
        error: 'Process stopped',
      };
    }
    // Step 10: Align the length of the tunnels for opposite airflow directions
    tunnels = await step10AlignTunnelsLengthAndWidth(
      botData,
      ahuId,
      tunnels,
      accessDoorParams.min_width,
      getOptions,
      (coeff) => progressCalculator.calculateCompletedPercent(coeff),
      checkStopped,
    );
    if (checkStopped()) {
      return {
        success: false,
        ahuId,
        tunnelIds,
        error: 'Process stopped',
      };
    }
    //if (botData.floor_construction_id === 2) {
      // Re-validate tunnels: repeat step 5 validation to ensure tunnel dimensions
      // are still valid after all components and sections have been added
      // This is necessary because raised floor construction can impact tunnel sizing requirements
      tunnels = await step5ValidateAndFixTunnels(
        tunnels,
        tunnelIds,
        botData.tunnels,
        false,
        getOptions,
        (coeff) => progressCalculator.calculateCompletedPercent(coeff),
      );
      // Check if the process was stopped during validation
      if (checkStopped()) {
        return {
          success: false,
          ahuId,
          tunnelIds,
          error: 'Process stopped',
        };
      }
    //}
    progressCalculator.calculateCompletedPercent(0, 99);
    await delay(1000);
    progressCalculator.calculateCompletedPercent(0, 100);
    return {
      success: true,
      ahuId,
      tunnelIds,
    };
  } catch (error) {
    console.log(error);
    // Check if error is due to cancellation
    if (
      (error instanceof Error && error.name === 'AbortError') ||
      abortSignal?.aborted
    ) {
      return {
        success: false,
        ahuId,
        tunnelIds: tunnelIds.length > 0 ? tunnelIds : undefined,
        error: 'Process stopped',
      };
    }
    // Get error message
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error occurred';
    return {
      success: false,
      ahuId,
      tunnelIds: tunnelIds.length > 0 ? tunnelIds : undefined,
      error: errorMessage,
    };
  }
};
